import type { PointerEvent as ReactPointerEvent } from 'react';
import { startDrag } from './drag.js';
import {
  MIN_H,
  MIN_W,
  PRESET_COLORS,
  edgeGeometry,
  snap,
  type CanvasEdge,
  type CanvasModel,
  type CanvasNode,
  type Side,
  type Viewport,
} from './model.js';
import type { CanvasStore } from './store.js';
import { Icon } from '../mo/Icon.js';

const stop = (e: ReactPointerEvent) => e.stopPropagation();
const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), Math.max(lo, hi));

function ColorBar({
  current,
  onPick,
}: {
  current: string | undefined;
  onPick: (c: string | undefined) => void;
}) {
  return (
    <>
      {Object.entries(PRESET_COLORS).map(([key, hex]) => (
        <button
          key={key}
          className={`cv-bar-btn cv-swatch${current === key ? ' cv-on' : ''}`}
          title={`Color ${key}`}
          onClick={() => onPick(key)}
        >
          <span className="cv-dot" style={{ background: hex }} />
        </button>
      ))}
      <button
        className={`cv-bar-btn cv-swatch${!current ? ' cv-on' : ''}`}
        title="Clear color"
        onClick={() => onPick(undefined)}
      >
        <span className="cv-dot cv-dot-none" />
      </button>
    </>
  );
}

// ---------------------------------------------------------------------------

export interface NodeChromeProps {
  node: CanvasNode;
  viewport: Viewport;
  paneSize: { width: number; height: number };
  store: CanvasStore;
  onDelete: () => void;
  onEdgeStart: (nodeId: string, side: Side, e: ReactPointerEvent) => void;
}

/** Screen-space chrome for the selected node: floating action bar (color
 * presets + delete), corner resize handles, side ○ edge-creation handles. */
export function NodeChrome({ node, viewport, paneSize, store, onDelete, onEdgeStart }: NodeChromeProps) {
  const s = viewport.scale;
  const sx = node.x * s + viewport.x;
  const sy = node.y * s + viewport.y;
  const sw = node.width * s;
  const sh = node.height * s;

  const setColor = (c: string | undefined) =>
    store.apply((m) => {
      const n = m.nodes.find((x) => x.id === node.id);
      if (n) n.color = c;
    });

  const onResize = (corner: 'nw' | 'ne' | 'sw' | 'se') => (e: ReactPointerEvent) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.stopPropagation();
    const start = { x: node.x, y: node.y, w: node.width, h: node.height };
    const cx = e.clientX;
    const cy = e.clientY;
    store.beginInteraction();
    startDrag(e, {
      move: (ev) => {
        const dx = (ev.clientX - cx) / s;
        const dy = (ev.clientY - cy) / s;
        store.apply((m) => {
          const n = m.nodes.find((x) => x.id === node.id);
          if (!n) return;
          let { x, y, w, h } = { x: start.x, y: start.y, w: start.w, h: start.h };
          if (corner.includes('w')) {
            const nx = Math.min(snap(start.x + dx), start.x + start.w - MIN_W);
            w = start.w + (start.x - nx);
            x = nx;
          }
          if (corner.includes('e')) w = Math.max(MIN_W, snap(start.w + dx));
          if (corner.includes('n')) {
            const ny = Math.min(snap(start.y + dy), start.y + start.h - MIN_H);
            h = start.h + (start.y - ny);
            y = ny;
          }
          if (corner.includes('s')) h = Math.max(MIN_H, snap(start.h + dy));
          n.x = x;
          n.y = y;
          n.width = w;
          n.height = h;
        });
      },
      up: () => store.endInteraction(),
    });
  };

  const barBelow = sy < 68;
  const barX = clamp(sx + sw / 2, 172, paneSize.width - 172);
  const corners = [
    ['nw', sx, sy],
    ['ne', sx + sw, sy],
    ['sw', sx, sy + sh],
    ['se', sx + sw, sy + sh],
  ] as const;
  const sides = [
    ['top', sx + sw / 2, sy],
    ['right', sx + sw, sy + sh / 2],
    ['bottom', sx + sw / 2, sy + sh],
    ['left', sx, sy + sh / 2],
  ] as const;

  return (
    <div className="cv-chrome" onPointerDown={stop}>
      <div
        className="cv-actionbar"
        style={{
          left: barX,
          top: barBelow ? sy + sh + 12 : sy - 12,
          transform: barBelow ? 'translate(-50%, 0)' : 'translate(-50%, -100%)',
        }}
      >
        <ColorBar current={node.color} onPick={setColor} />
        <button className="cv-bar-btn cv-danger" title="Delete node" onClick={onDelete}><Icon name="close" size={15} /></button>
      </div>
      {corners.map(([corner, x, y]) => (
        <div
          key={corner}
          className={`cv-handle cv-corner cv-${corner}`}
          style={{ left: x, top: y }}
          onPointerDown={onResize(corner)}
        />
      ))}
      {sides.map(([side, x, y]) => (
        <div
          key={side}
          className="cv-handle cv-sidehandle"
          title="Drag to connect"
          style={{ left: x, top: y }}
          onPointerDown={(e) => {
            if (e.pointerType === 'mouse' && e.button !== 0) return;
            e.stopPropagation();
            onEdgeStart(node.id, side, e);
          }}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------

export interface EdgeChromeProps {
  edge: CanvasEdge;
  model: CanvasModel;
  viewport: Viewport;
  paneSize: { width: number; height: number };
  store: CanvasStore;
  onDelete: () => void;
  onEditLabel: (id: string) => void;
}

/** Floating action bar for the selected edge: color, label, delete. */
export function EdgeChrome({ edge, model, viewport, paneSize, store, onDelete, onEditLabel }: EdgeChromeProps) {
  const a = model.nodes.find((n) => n.id === edge.fromNode);
  const b = model.nodes.find((n) => n.id === edge.toNode);
  if (!a || !b) return null;
  const g = edgeGeometry(a, b, edge.fromSide, edge.toSide);
  const sx = g.mid.x * viewport.scale + viewport.x;
  const sy = g.mid.y * viewport.scale + viewport.y;

  const setColor = (c: string | undefined) =>
    store.apply((m) => {
      const ed = m.edges.find((x) => x.id === edge.id);
      if (ed) ed.color = c;
    });

  return (
    <div className="cv-chrome" onPointerDown={stop}>
      <div
        className="cv-actionbar"
        style={{
          left: clamp(sx, 192, paneSize.width - 192),
          top: clamp(sy - 16, 56, Math.max(56, paneSize.height - 12)),
          transform: 'translate(-50%, -100%)',
        }}
      >
        <ColorBar current={edge.color} onPick={setColor} />
        <button className="cv-bar-btn" title="Edit label" onClick={() => onEditLabel(edge.id)}><Icon name="edit" size={15} /></button>
        <button className="cv-bar-btn cv-danger" title="Delete edge" onClick={onDelete}><Icon name="close" size={15} /></button>
      </div>
    </div>
  );
}
